<template>
  <Modal
    event-tracking-source="search popup"
    :value="value"
    @input="$emit('input', $event)"
  >
    <template slot="header">
      <SearchHeader
        ref="header"
        v-model="query"
        @close="$emit('input', false)"
        @submit="submitSearchForm"
      />
    </template>

    <template slot="default">
      <div>
        <ul
          v-if="query.trim().length > 0"
          class="py-2"
        >
          <Result
            v-for="categoryType in SearchCategories"
            :key="categoryType.id"
            :link="categorySearchLink(categoryType)"
            @click="search(categorySearchLink(categoryType))"
          >
            {{ query }} in {{ categoryType.name }}
          </Result>
        </ul>

        <div
          v-if="loading"
          class="py-2 text-center"
          style="border-top: 1px solid #4D4D4D;"
        >
          <i
            class="fad fa-spinner-third fa-spin text-white text-2xl"
          />
        </div>

        <ul
          v-else-if="options.length > 0"
          class="py-2"
          style="border-top: 1px solid #4D4D4D;"
        >
          <Result
            v-for="option in options"
            :key="option"
            :link="`/browse/search/${option}/`"
            @click="clickOption(option)"
          >
            {{ option }}
          </Result>
        </ul>
      </div>
    </template>
  </Modal>
</template>

<script>
import analyticsEvents from '@/utils/analyticsEvents';
import { eventTracker } from '@/utils/eventTracker';
import browseEventsData from '@/utils/events/browse-events-data';
import axios from 'axios';
import debounce from 'lodash/debounce';
import { CATEGORY_SLUG_TO_CATEGORY_TYPE_MAP, SEARCH_TYPES, SearchCategories } from '@/constants';
import Modal from './modal.vue';
import SearchHeader from './header.vue';
import Result from './result.vue';

export default {
  components: {
    Modal,
    SearchHeader,
    Result,
  },

  props: {
    value: {
      type: Boolean,
      default: false,
    },
  },

  data() {
    return {
      query: '',
      loading: false,
      options: [],
    };
  },
  computed: {
    SearchCategories: () => SearchCategories,
  },
  watch: {
    query(newVal) {
      if (newVal.length >= 3) {
        this.debouncedGetSuggestions(newVal);
      } else if (newVal.length === 0) {
        this.options = [];
      }
    },
  },
  mounted() {
    this.$refs.header.$refs.input.focus();
  },
  methods: {
    submitSearchForm() {
      this.$store.dispatch('search/setSearchParams', { type: SEARCH_TYPES.GLOBAL_SEARCH });

      const eventProperties = {
        ...browseEventsData({
          $route: this.$route,
          location: 'header',
          categorySlug: this.$store.getters.categoryFilters.slug || '',
        }),
        search_term: this.query,
        location: 'header',
      };
      eventTracker(analyticsEvents.freeTextMediaSearched, eventProperties);
      this.search(`/browse/search/${this.query}/`);
    },
    clickOption(option) {
      const eventProperties = {
        ...browseEventsData({
          $route: this.$route,
          location: 'header',
          categorySlug: this.$store.getters.categoryFilters.slug || '',
        }),
        search_term: this.query,
        location: 'header',
        original_search_term: option,
      };
      eventTracker(analyticsEvents.suggestionSearchTermSelected, eventProperties);
      this.search(`/browse/search/${option}/`);
    },
    search(link) {
      if (!this.query.trim()) {
        return;
      }

      const eventProperties = {
        ...browseEventsData({
          $route: this.$route,
          location: 'header',
          categorySlug: this.$store.getters.categoryFilters.slug || '',
        }),
        search_term: this.query,
        location: 'header',
      };
      eventTracker(analyticsEvents.searchTermTyped, eventProperties);

      window.location.href = link;
    },

    getSuggestions(query) {
      const url = `/api/tags/search?tag-query=${query}&limit=6`;
      return axios.get(url).then(({ data }) => {
        if (data.length === 0) {
          const eventProperties = {
            location: 'header',
            search_term: query,
            category: CATEGORY_SLUG_TO_CATEGORY_TYPE_MAP[this.$store.getters.categoryFilters.slug]?.toLowerCase(),
          };
          eventTracker(analyticsEvents.searchAutoCompleteReturnedEmpty, eventProperties);
        }

        return data;
      });
    },

    debouncedGetSuggestions: debounce(function (query) {
      if (this.loading || query.length < 3) {
        return;
      }

      this.loading = true;

      this.getSuggestions(query)
        .then((options) => {
          // Modal is closed before the request is finished.
          // Data is reset, so we don't need want to do anything.
          if (!this.value) {
            return;
          }

          this.options = options;
          this.loading = false;
        })
        .finally(() => {
          this.loading = false;
        });
    }, 300),

    categorySearchLink(categoryType) {
      if (categoryType.slug) {
        return `/browse/${categoryType.slug}/?q=${this.query}&search_header=1`;
      } else {
        return `/browse/search/${this.query}/?category=${categoryType.id}`;
      }
    },
  },
};
</script>
